import React from "react"
import { Box, InternalIcon, LaptopIcon, Link, Tile, Typography } from "@carrotfertility/carotene-core"
import { FormattedMessage } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import {
  useTelemedicineAvailability,
  TelemedicineProvider
} from "#/components/telemedicine/hooks/useTelemedicineAvailability"
import { useProviderTypeSearched } from "#/pages/provider-finder/components/results/useProviderTypeSearched"
import { Paths } from "#/utils/Paths"
import { useFlags } from "launchdarkly-react-client-sdk"

const PROVIDER_NAMES = {
  MEDICAL_PRIME: "Medical Prime",
  THE_MENOPAUSE_HUB: "The Menopause Hub"
} as const

const getBannerContent = (provider: TelemedicineProvider | undefined = undefined) => {
  switch (provider) {
    case TelemedicineProvider.MedicalPrime:
      return {
        headerText: (
          <FormattedMessage
            defaultMessage="Get virtual menopause care at {providerName}"
            values={{ providerName: PROVIDER_NAMES.MEDICAL_PRIME }}
          />
        ),
        descriptionText: (
          <FormattedMessage
            defaultMessage="{providerName} offers appointments for virtual and in-person menopause care, including hormone therapy (HT), within just four weeks. Carrot members get 10% off their first consultation."
            values={{ providerName: PROVIDER_NAMES.MEDICAL_PRIME }}
          />
        ),
        linkText: (
          <FormattedMessage
            defaultMessage="Learn more about menopause care with {providerName}"
            values={{ providerName: PROVIDER_NAMES.MEDICAL_PRIME }}
          />
        )
      }
    case TelemedicineProvider.TheMenopauseHub:
      return {
        headerText: (
          <FormattedMessage
            defaultMessage="Get virtual menopause care at {providerName}"
            values={{ providerName: PROVIDER_NAMES.THE_MENOPAUSE_HUB }}
          />
        ),
        descriptionText: (
          <FormattedMessage
            defaultMessage="{providerName} offers appointments for virtual and in-person menopause care, including hormone therapy (HT), within just one week. Carrot members get 10% off consultations."
            values={{ providerName: PROVIDER_NAMES.THE_MENOPAUSE_HUB }}
          />
        ),
        linkText: (
          <FormattedMessage
            defaultMessage="Learn more about menopause care with {providerName}"
            values={{ providerName: PROVIDER_NAMES.THE_MENOPAUSE_HUB }}
          />
        )
      }
    case TelemedicineProvider.Wheel:
    default:
      return {
        headerText: <FormattedMessage defaultMessage="Get care at Carrot’s virtual menopause clinic" />,
        descriptionText: (
          <FormattedMessage defaultMessage="Are you between the ages of 40 and 75? Get virtual menopause care, including hormone therapy (HT) prescriptions when appropriate, from the comfort of your home." />
        ),
        linkText: <FormattedMessage defaultMessage="Learn more about virtual menopause care" />
      }
  }
}

export function TelemedicineForMenopausePfBanner(): JSX.Element {
  const { data: telemedicineAvailability } = useTelemedicineAvailability()
  const { isMenopauseSearched } = useProviderTypeSearched()
  const { telemedicineEnterprisePayments } = useFlags()

  const availableProviders: TelemedicineProvider[] = Array.isArray(telemedicineAvailability?.availableProviders)
    ? telemedicineAvailability.availableProviders
    : []

  const telemedicineProvider: TelemedicineProvider | undefined = availableProviders.find(
    (provider: TelemedicineProvider) => Object.values(TelemedicineProvider).includes(provider)
  )

  if (telemedicineEnterprisePayments && telemedicineProvider === TelemedicineProvider.Wheel) {
    return null
  }

  const bannerContent = getBannerContent(telemedicineProvider)

  const getLinkDestination = () => {
    if ([TelemedicineProvider.MedicalPrime, TelemedicineProvider.TheMenopauseHub].includes(telemedicineProvider)) {
      return Paths.TELEMEDICINE_FOR_MENOPAUSE_CARE
    }
    return `${Paths.PROVIDER_FINDER}/telemedicine-for-menopause`
  }

  return isMenopauseSearched && telemedicineAvailability?.isAvailable && telemedicineProvider ? (
    <Tile color="primary">
      <Box
        sx={(theme) => ({
          display: "flex",
          direction: "row",
          marginBlockEnd: theme.spacing(theme.tokens.spacing.xs)
        })}
      >
        <LaptopIcon />
        <Typography sx={(theme) => ({ marginInlineStart: theme.spacing(theme.tokens.spacing.xxs) })} variant="h5">
          {bannerContent.headerText}
        </Typography>
      </Box>
      <Typography sx={(theme) => ({ marginBlockEnd: theme.spacing(theme.tokens.spacing.md) })}>
        {bannerContent.descriptionText}
      </Typography>
      <Link
        component={RouterLink}
        to={getLinkDestination()}
        endIcon={<InternalIcon color="primary" fontSize="small" />}
      >
        {bannerContent.linkText}
      </Link>
    </Tile>
  ) : null
}
